import TicketsResource from 'flow/api/omnitickets';
import Actions from 'flow/actions';
import { Map, fromJS, List, Set as ImmutableSet } from 'immutable';
import { REGEXP_POSSIBLE_MENTION_STRICT } from 'flow/utils/message-utils';
import { TICKET_FAILED_EVENTS_LOCAL_STORAGE_KEY } from 'flow/utils/browser-utils';
import { ChatUserKey, ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import {
    PickedMention,
    TicketMessageChannel,
    TicketReplyType,
} from 'flow/types/Tickets';
import { State } from 'flow/types/State';
import { networkErrorDispatcher } from 'flow/actions/app-actions';
import { onTicketWriteRefused } from 'flow/actions/ticket-actions';
import { ticketRequestError } from 'flow/utils/ticket-utils';
import {
    setTicketMessageStatus,
    fetchTicketMessage,
} from 'flow/actions/tickets-action';
import {
    selectChatUserKey,
    selectContact,
    selectExtensions,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import {
    selectMembers,
    selectNonMembersAreMentionable,
} from 'flow/selectors/TicketsSelectors';
import {
    selectEvents,
    selectTicketMessageAttachments,
} from 'flow/selectors/TicketEventSelectors';
import {
    ConversationMessagePlaceholderKey,
    EmailRecipient,
    EmailSentResponse,
    InternalNoteSentResponse,
    MessageStyling,
    PREFIX_MESSAGE_PLACEHOLDER_KEY,
    SenderData,
    SmsSentResponse,
    TicketAttachmentPlaceholder,
    TicketEventMessageDtoSubtype,
    WhatsAppMessageSentResponse,
    WhatsAppMessageTemplateSentResponse,
} from 'flow/types/TicketEventData';
import AttachmentUploadError, {
    ERROR_GENERIC,
    FILE_NOT_ACCEPTED,
    FILE_SIZE_EXCEEDED,
    FILE_TYPE_NOT_ALLOWED,
} from './Attachments/AttachmentUploadError';
import { browserSupportStorage } from 'flow/utils/browser-storage-utils';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import { escapeRegExp } from 'flow/utils/message-utils';
import { getContactName } from 'flow/utils/contact-utils';
import { AppDispatch } from 'flow/types/Redux';
import { TypedMap } from 'flow/types/TypedMap';

interface EmailReplyRecipients {
    toRecipients?: EmailRecipient[];
    ccRecipients?: EmailRecipient[];
}

export const fetchEmailRecipients = (
    ticketKey: TicketKey,
): Promise<EmailReplyRecipients> => {
    return new Promise((resolve, reject) => {
        TicketsResource.fetchEmailRecipients(
            {
                onResult(data: EmailReplyRecipients) {
                    resolve(data);
                },
                onClientError: (error?: Response) => {
                    reject(new Error(`${error?.status}`));
                },
                onError: (error?: Response) => {
                    reject(new Error(`${error?.status}`));
                },
            },
            ticketKey,
        );
    });
};

export const MAX_ATTACHMENT_SIZE_DEFAULT = 20971520;

const fetchAttachmentUploadUrl = (
    ticketKey: TicketKey,
    channel: TicketMessageChannel,
    fileName: string,
    fileSize: number,
    mimeType: string,
) => {
    return new Promise((resolve, reject) => {
        TicketsResource.getUploadUrl(
            {
                onResult(data: ResponseGetUploadUrl) {
                    resolve(data);
                },
                onClientError: (error: Response) => {
                    reject(error);
                },
                onError: (error: Response) => {
                    reject(error);
                },
            },
            ticketKey,
            fileName,
            channel,
            fileSize,
            mimeType,
        );
    });
};

interface ResponseGetUploadUrl {
    url: string;
    path: string;
    isAccepted: boolean;
    maxSize: number;
    isAllowedType: boolean;
}

export const uploadAttachmentAndGetAttachmentData = async (
    ticketKey: TicketKey,
    channel: TicketMessageChannel,
    attachment?: { file: File; name: string; aspectRatio?: number },
) => {
    if (!attachment?.file) return null;

    const fileName = attachment.name;
    const file = attachment.file;

    let responseData: ResponseGetUploadUrl;
    try {
        responseData = (await fetchAttachmentUploadUrl(
            ticketKey,
            channel,
            fileName,
            file.size,
            file.type,
        )) as ResponseGetUploadUrl;
    } catch (e) {
        throw new AttachmentUploadError(ERROR_GENERIC, e?.message);
    }
    const { url, path, maxSize, isAllowedType, isAccepted } = responseData;

    // maxSize = if the file is the correct type, but is too big
    // isAllowedType = if the file type is supported
    // isAccepted = if the file is the correct type and size

    if (maxSize)
        throw new AttachmentUploadError(
            FILE_SIZE_EXCEEDED,
            'File size exceeded',
        );
    if (!isAllowedType)
        throw new AttachmentUploadError(
            FILE_TYPE_NOT_ALLOWED,
            'File type not allowed',
        );
    if (!isAccepted)
        throw new AttachmentUploadError(FILE_NOT_ACCEPTED, 'File not accepted');
    // We should never end up in this last !isAccepted block, as isAccepted is
    // false only if either maxSize has a number, or if isAllowedType is false. These
    // two are already handled above. But just in case, we throw an error.

    try {
        await fetch(url, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': 'application/octet-stream' },
        });
    } catch (e) {
        throw new AttachmentUploadError(ERROR_GENERIC, e?.message);
    }

    return {
        fileName,
        fileSize: file.size,
        fileType: file.type,
        path,
        aspectRatio: attachment.aspectRatio,
    };
    // Aspect ratio relevant for the client to use the appropriate amount of space
    // for the placeholder message that is displayed between the user pressing 'send',
    // and the server responding.
};

export const fetchTicketAttachmentRestrictions =
    () => (dispatch: AppDispatch) =>
        new Promise((resolve, reject) => {
            TicketsResource.getFileRestrictions({
                onResult(response: Restrictions[]) {
                    dispatch(setAttachmentRestrictions(response));
                    resolve(response);
                },
                onClientError: (error: Response) => {
                    reject(error);
                },
                onError: (error: Response) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            });
        });

const setAttachmentRestrictions =
    (data: Restrictions[]) => (dispatch: AppDispatch) =>
        dispatch({
            type: Actions.SET_TICKET_ATTACHMENT_SEND_RESTRICTIONS,
            data,
        });

interface Restrictions {
    blockedMimetypes: List<string>;
    allowedMimeTypes: Record<string, { maxSizeBytes: number }>;
    channel: string;
}

export const getFileExtensionWithDot = (fileName?: string) => {
    if (!fileName) return '';
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex !== -1 ? fileName.slice(lastDotIndex) : '';
};

const getSendMessageActionFromDTOSubtype = (
    dtoSubtype: TicketEventMessageDtoSubtype,
) => {
    if (dtoSubtype === 'OmniEmailMessageTicketEvent')
        return TicketsResource.sendEmailMessage;
    if (dtoSubtype === 'OmniWhatsAppMessageTicketEvent')
        return TicketsResource.sendWhatsAppMessage;
    if (dtoSubtype === 'OmniInternalNoteTicketEvent')
        return TicketsResource.addInternalNote;
    if (dtoSubtype === 'OmniSmsNoteTicketEvent') return TicketsResource.sendSMS;
    if (dtoSubtype === 'OmniWhatsAppTemplateMessageTicketEvent')
        return TicketsResource.sendWhatsAppTemplateMessage;
    return undefined;
};

const getChannelFromDTOSubtype = (dtoSubtype: TicketEventMessageDtoSubtype) => {
    if (dtoSubtype === 'OmniEmailMessageTicketEvent') return 'email';
    if (
        dtoSubtype === 'OmniWhatsAppMessageTicketEvent' ||
        dtoSubtype === 'OmniWhatsAppTemplateMessageTicketEvent'
    )
        return 'whatsapp';
    if (dtoSubtype === 'OmniInternalNoteTicketEvent') return 'internal';
    if (dtoSubtype === 'OmniSmsNoteTicketEvent') return 'sms';
    return undefined;
};

export const tryResendTicketMessage =
    (ticketKey: TicketKey, messageKey: ConversationMessagePlaceholderKey) =>
    async (dispatch: AppDispatch, getState: () => State) => {
        const messageEventBase = selectEvents(getState(), ticketKey)
            ?.find((event) => event.get('key') === messageKey)
            ?.delete('key');

        const attachment = selectTicketMessageAttachments(
            getState(),
            ticketKey,
            messageKey,
        )?.first();

        const eventWithUploadedAttachmentData = attachment?.get('path')
            ? fromJS({ attachments: List([attachment]) }).merge(
                  messageEventBase,
              )
            : messageEventBase;
        // if attachment path property has a value, the attachment has been uploaded to the bucket,
        // and we should add this data to the event that is sent to the API.

        const channel = getChannelFromDTOSubtype(
            messageEventBase?.get('DTOSubtype'),
        );
        dispatch(setTicketMessageStatus(ticketKey, messageKey, 'SENDING'));
        // Not caught here: sendMessage has already marked the placeholder failed,
        // and the rejection is what tells the caller to explain the refusal.
        await dispatch(
            sendMessage(
                ticketKey,
                messageKey,
                channel,
                eventWithUploadedAttachmentData?.toJS(),
                null,
                null, //event already saved when resend action is presented
                true,
            ),
        );
    };

interface AttachmentDataFromInput {
    file: File;
    name: string;
    aspectRatio?: number;
}

const getAttachmentPlaceholderFriendly = (
    attachmentData: AttachmentDataFromInput,
): TicketAttachmentPlaceholder => {
    if (!attachmentData) return null;
    return {
        fileName: attachmentData.name,
        fileSize: attachmentData.file.size,
        fileType: attachmentData.file.type,
        aspectRatio: attachmentData.aspectRatio,
    };
};

interface MessageEventToSendBase {
    DTOSubtype: TicketEventMessageDtoSubtype;
    message?: string;
    //sender: ExtensionKey;
}

const handleAttachmentUpload =
    (
        ticketKey: TicketKey,
        channel: TicketMessageChannel,
        messagePlaceholderKey: ConversationMessagePlaceholderKey,
        attachmentToUpload?: AttachmentDataFromInput,
    ) =>
    async (dispatch: AppDispatch) => {
        const attachmentApiData = await uploadAttachmentAndGetAttachmentData(
            ticketKey,
            channel,
            attachmentToUpload,
        );
        if (attachmentApiData) {
            dispatch(
                onUploadAttachmentStageCompleted(
                    ticketKey,
                    messagePlaceholderKey,
                    attachmentApiData,
                ),
            );
        }
        return attachmentApiData;
    };

interface BaseMessageEventJS {
    DTOSubtype: TicketEventMessageDtoSubtype;
    message?: string;
    //sender: ExtensionKey;
    stylings?: List<TypedMap<MessageStyling>>;
}

interface PlaceholderMessageBase {
    message?: string;
    key: ConversationMessagePlaceholderKey;
    read?: boolean; //Not yet relevant
    sender: ExtensionKey;
    senderData: SenderData;
    status: 'SENDING' | 'FAILED';
    attachments: TicketAttachmentPlaceholder[];
}

interface PlaceholderEmailJS extends PlaceholderMessageBase {
    DTOSubtype: 'OmniEmailMessageTicketEvent';
    direction?: 'OUTGOING';
}

interface PlaceholderInternalNoteJS extends PlaceholderMessageBase {
    DTOSubtype: 'OmniInternalNoteTicketEvent';
    stylings?: List<TypedMap<MessageStyling>>;
}

interface PlaceholderSmsMessageJS extends PlaceholderMessageBase {
    DTOSubtype: 'OmniSmsNoteTicketEvent';
    direction: 'OUTGOING';
}

interface PlaceholderWhatsAppMessageJS extends PlaceholderMessageBase {
    DTOSubtype: 'OmniWhatsAppMessageTicketEvent';
    direction: 'OUTGOING';
}

interface PlaceholderWhatsAppTemplateMessageJS extends PlaceholderMessageBase {
    DTOSubtype: 'OmniWhatsAppTemplateMessageTicketEvent';
    direction: 'OUTGOING';
}

type PlaceholderMessage =
    | PlaceholderEmailJS
    | PlaceholderInternalNoteJS
    | PlaceholderSmsMessageJS
    | PlaceholderWhatsAppMessageJS
    | PlaceholderWhatsAppTemplateMessageJS;

type SendMessageEventResponse =
    | EmailSentResponse
    | InternalNoteSentResponse
    | SmsSentResponse
    | WhatsAppMessageSentResponse
    | WhatsAppMessageTemplateSentResponse;

const sendMessage =
    (
        ticketKey: TicketKey,
        messagePlaceholderKey: ConversationMessagePlaceholderKey,
        channel: TicketMessageChannel,
        messageDTOBase: MessageEventToSendBase,
        attachmentToUpload?: AttachmentDataFromInput,
        placeholderEventToSaveLocallyOnFail?: PlaceholderMessage,
        isRetryAttempt?: boolean,
    ) =>
    async (dispatch: AppDispatch) => {
        let attachmentApiData;
        try {
            attachmentApiData = await dispatch(
                handleAttachmentUpload(
                    ticketKey,
                    channel,
                    messagePlaceholderKey,
                    attachmentToUpload,
                ),
            );
        } catch (error) {
            if (error instanceof AttachmentUploadError) {
                if (
                    error.code === FILE_TYPE_NOT_ALLOWED ||
                    error.code === FILE_NOT_ACCEPTED ||
                    error.code === FILE_SIZE_EXCEEDED
                ) {
                    dispatch(
                        onUnrecoverableSendError(
                            ticketKey,
                            messagePlaceholderKey,
                        ),
                    );
                    // If error code matches any of the above, we remove the placeholder message as any
                    // resend attempts are guaranteed to fail
                } else {
                    dispatch(
                        onMessageFailedToSend(
                            ticketKey,
                            messagePlaceholderKey,
                            placeholderEventToSaveLocallyOnFail,
                        ),
                    );
                }
                throw error;
            }
            dispatch(
                onMessageFailedToSend(
                    ticketKey,
                    messagePlaceholderKey,
                    placeholderEventToSaveLocallyOnFail,
                ),
            );
            networkErrorDispatcher(dispatch);
            //Should never end up here, I believe.
            return;
        }

        const messageDTOForApi = {
            ...messageDTOBase,
            ...(attachmentApiData ? { attachments: [attachmentApiData] } : {}),
        };

        const sendMessageAction = getSendMessageActionFromDTOSubtype(
            messageDTOForApi.DTOSubtype,
        );

        // A failed send marks its placeholder and also rejects, so the
        // caller hears about it. That is what lets useTicketAction tell the
        // user when the backend refused the message.
        await new Promise<void>((resolve, reject) => {
            const onSendFailed = (request?: { status?: number }) => {
                dispatch(
                    onMessageFailedToSend(
                        ticketKey,
                        messagePlaceholderKey,
                        placeholderEventToSaveLocallyOnFail,
                    ),
                );
                reject(ticketRequestError(request));
            };

            sendMessageAction(
                {
                    onResult(data: SendMessageEventResponse) {
                        // The send response body is plain text. For email we
                        // already composed the HTML locally (reply + signature),
                        // so carry it into the stored event to render HTML
                        // immediately — otherwise the plain response shows for a
                        // frame before the canonical HTML version is fetched.
                        const reconciledData =
                            data.DTOSubtype === 'OmniEmailMessageTicketEvent' &&
                            messageDTOForApi.message
                                ? {
                                      ...data,
                                      message: messageDTOForApi.message,
                                  }
                                : data;
                        dispatch(
                            onMessageSentSuccess(
                                ticketKey,
                                messagePlaceholderKey,
                                reconciledData,
                                isRetryAttempt,
                            ),
                        );
                        resolve();
                    },
                    onError: onSendFailed,
                    onClientError: (request?: { status?: number }) => {
                        dispatch(onTicketWriteRefused(ticketKey, request));
                        onSendFailed(request);
                    },
                },
                ticketKey,
                messageDTOForApi,
            );
        });
    };

const getPlaceholderMessageEventBase =
    (
        baseEvent: BaseMessageEventJS,
        placeholderKey: ConversationMessagePlaceholderKey,
        attachmentData?: AttachmentDataFromInput,
    ) =>
    (_dispatch: AppDispatch, getState: () => State) => {
        const ownExtensionKey = selectOwnExtensionKey(getState());
        return {
            ...baseEvent,
            sender: ownExtensionKey,
            senderData: getSenderDataForPlaceholderMessage(ownExtensionKey),
            key: placeholderKey,
            status: 'SENDING',
            attachments: attachmentData
                ? [getAttachmentPlaceholderFriendly(attachmentData)]
                : null,
        } as const;
    };

export const sendInternalNote =
    (
        ticketKey: TicketKey,
        message?: string,
        attachmentDataFromInput?: AttachmentDataFromInput,
        pickedMentions?: List<TypedMap<PickedMention>>,
    ) =>
    async (dispatch: AppDispatch, getState: () => State) => {
        const mentions =
            createMentionDTO(getState(), ticketKey, message, pickedMentions) ||
            List();

        const internalNoteEventBase = {
            DTOSubtype: 'OmniInternalNoteTicketEvent',
            message: message,
            stylings: mentions,
        } as const;

        const placeholderKey: ConversationMessagePlaceholderKey = `${PREFIX_MESSAGE_PLACEHOLDER_KEY}-${Date.now()}`;
        const placeholderInternalNoteDTO: PlaceholderInternalNoteJS = {
            ...dispatch(
                getPlaceholderMessageEventBase(
                    internalNoteEventBase,
                    placeholderKey,
                    attachmentDataFromInput,
                ),
            ),
            DTOSubtype: 'OmniInternalNoteTicketEvent',
        };

        dispatch(
            addPlaceholderEventToStore(ticketKey, placeholderInternalNoteDTO),
        );

        await dispatch(
            sendMessage(
                ticketKey,
                placeholderKey,
                'internal',
                internalNoteEventBase,
                attachmentDataFromInput,
                placeholderInternalNoteDTO,
            ),
        );
    };

const escapeHtmlForEmail = (text: string) =>
    text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// With a signature the reply is composed as HTML and declared 'rich'; the
// ticket service stores a stripped plaintext copy for mobile and the email
// service sends it as BodyType.Html. Without a signature the reply is sent
// as plain text declared 'plain', byte-identical to the pre-signature
// behavior, so customers without the feature flag are entirely unaffected.
export const buildEmailBody = (
    plainText: string,
    signature?: string,
): { message: string; messageContentType: 'rich' | 'plain' } => {
    if (!signature) return { message: plainText, messageContentType: 'plain' };
    const escapedBody = escapeHtmlForEmail(plainText).replace(/\n/g, '<br>');
    return {
        message: escapedBody ? `${escapedBody}<br>${signature}` : signature,
        messageContentType: 'rich',
    };
};

export const onSendEmailRequested = (
    ticketKey: TicketKey,
    message?: string,
    attachmentDataFromInput?: AttachmentDataFromInput,
    emailSignature?: string,
) => {
    return async (dispatch: AppDispatch) => {
        const body = buildEmailBody(message ?? '', emailSignature);

        const emailEventBase = {
            DTOSubtype: 'OmniEmailMessageTicketEvent',
            message: body.message,
            messageContentType: body.messageContentType,
        } as const;

        const placeholderKey: ConversationMessagePlaceholderKey = `${PREFIX_MESSAGE_PLACEHOLDER_KEY}-${Date.now()}`;
        const placeholderEmailDTO: PlaceholderEmailJS = {
            ...dispatch(
                getPlaceholderMessageEventBase(
                    emailEventBase,
                    placeholderKey,
                    attachmentDataFromInput,
                ),
            ),
            DTOSubtype: 'OmniEmailMessageTicketEvent',
        };

        dispatch(addPlaceholderEventToStore(ticketKey, placeholderEmailDTO));

        await dispatch(
            sendMessage(
                ticketKey,
                placeholderKey,
                'email',
                emailEventBase,
                attachmentDataFromInput,
                placeholderEmailDTO,
            ),
        );
    };
};

export const onSendWhatsAppMessageRequested =
    (
        ticketKey: TicketKey,
        message?: string,
        attachmentDataFromInput?: AttachmentDataFromInput,
    ) =>
    async (dispatch: AppDispatch) => {
        const whatsAppEventBase = {
            DTOSubtype: 'OmniWhatsAppMessageTicketEvent',
            message: message,
        } as const;

        const containsAudioAttachment =
            attachmentDataFromInput?.file?.type?.startsWith('audio') ?? false;

        const placeholderKey: ConversationMessagePlaceholderKey = `${PREFIX_MESSAGE_PLACEHOLDER_KEY}-${Date.now()}`;

        // About if below:
        // When we send audio attachments in WhatsApp messages, we send them as type AUDIO (ish).
        // This makes it so that the audio can be played directly in the recipient's WhatsApp app.
        // One quirk with this, however, is that WhatsApp discards the message text in these types of messages.
        // So, when the user sends an audio attachment AND text, we send two events:
        // First, a WhatsApp message event with the text, but without the audio attachment.
        // Second, a WhatsApp message event with audio attachment, but no text.
        if (!containsAudioAttachment || (containsAudioAttachment && !message)) {
            const placeholderWhatsAppEvent: PlaceholderWhatsAppMessageJS = {
                ...dispatch(
                    getPlaceholderMessageEventBase(
                        whatsAppEventBase,
                        placeholderKey,
                        attachmentDataFromInput,
                    ),
                ),
                direction: 'OUTGOING',
                DTOSubtype: 'OmniWhatsAppMessageTicketEvent',
            };

            dispatch(
                addPlaceholderEventToStore(ticketKey, placeholderWhatsAppEvent),
            );

            await dispatch(
                sendMessage(
                    ticketKey,
                    placeholderKey,
                    'whatsapp',
                    whatsAppEventBase,
                    attachmentDataFromInput,
                    placeholderWhatsAppEvent,
                ),
            );
        } else {
            const placeholderKey2: ConversationMessagePlaceholderKey = `${PREFIX_MESSAGE_PLACEHOLDER_KEY}-${Date.now() + 1}`; //+1 to ensure that the key is different from the key used for the first event
            const placeholderEventWithTextOnly: PlaceholderWhatsAppMessageJS = {
                ...dispatch(
                    getPlaceholderMessageEventBase(
                        whatsAppEventBase,
                        placeholderKey,
                    ),
                ),
                direction: 'OUTGOING',
                DTOSubtype: 'OmniWhatsAppMessageTicketEvent',
            };

            // Second event: with attachment, no message
            const whatsAppEventBaseNoMessage = {
                DTOSubtype: 'OmniWhatsAppMessageTicketEvent',
            } as const;

            const placeholderEventWithAudioAttachmentOnly: PlaceholderWhatsAppMessageJS =
                {
                    ...dispatch(
                        getPlaceholderMessageEventBase(
                            whatsAppEventBaseNoMessage,
                            placeholderKey2,
                            attachmentDataFromInput,
                        ),
                    ),
                    direction: 'OUTGOING',
                    DTOSubtype: 'OmniWhatsAppMessageTicketEvent',
                };

            dispatch(
                addPlaceholderEventToStore(
                    ticketKey,
                    placeholderEventWithTextOnly,
                ),
            );
            dispatch(
                addPlaceholderEventToStore(
                    ticketKey,
                    placeholderEventWithAudioAttachmentOnly,
                ),
            );

            // Both placeholders are on screen already, and the sends stay in this
            // order because the text has to arrive before the audio. So a failed
            // text send is held rather than thrown: without the second send its
            // placeholder would sit at SENDING for good, with no failed state and
            // so no way to retry it.
            let textSendError;
            try {
                await dispatch(
                    sendMessage(
                        ticketKey,
                        placeholderKey,
                        'whatsapp',
                        whatsAppEventBase,
                        undefined,
                        placeholderEventWithTextOnly,
                    ),
                );
            } catch (error) {
                textSendError = error;
            }

            await dispatch(
                sendMessage(
                    ticketKey,
                    placeholderKey2,
                    'whatsapp',
                    whatsAppEventBaseNoMessage,
                    attachmentDataFromInput,
                    placeholderEventWithAudioAttachmentOnly,
                ),
            );

            if (textSendError) {
                throw textSendError;
            }
        }
    };

export const sendSMSMessage =
    (
        ticketKey: TicketKey,
        message?: string,
        attachmentDataFromInput?: AttachmentDataFromInput,
    ) =>
    async (dispatch: AppDispatch) => {
        const smsEventBase = {
            DTOSubtype: 'OmniSmsNoteTicketEvent',
            message: message,
        } as const;

        const placeholderKey: ConversationMessagePlaceholderKey = `${PREFIX_MESSAGE_PLACEHOLDER_KEY}-${Date.now()}`;
        const placeholderSmsEvent: PlaceholderSmsMessageJS = {
            ...dispatch(
                getPlaceholderMessageEventBase(
                    smsEventBase,
                    placeholderKey,
                    attachmentDataFromInput,
                ),
            ),
            direction: 'OUTGOING',
            DTOSubtype: 'OmniSmsNoteTicketEvent',
        };

        dispatch(addPlaceholderEventToStore(ticketKey, placeholderSmsEvent));

        await dispatch(
            sendMessage(
                ticketKey,
                placeholderKey,
                'sms',
                smsEventBase,
                attachmentDataFromInput,
                placeholderSmsEvent,
            ),
        );
    };

const addPlaceholderEventToStore =
    (ticketKey: TicketKey, placeholderEvent: PlaceholderMessage) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: Actions.SET_TICKET_EVENT,
            ticketKey,
            eventData: placeholderEvent,
        });
    };

const onMessageSentSuccess =
    (
        ticketKey: TicketKey,
        placeholderKey: ConversationMessagePlaceholderKey,
        eventData: SendMessageEventResponse,
        attemptDeleteFromLocalStorage?: boolean,
    ) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: Actions.SET_TICKET_EVENT,
            placeholderKeyToRemove: placeholderKey,
            ticketKey,
            eventData,
        });
        // The send response carries only the plain-text body; email messages
        // also have an HTML (rich) version that is not included here. Fetch the
        // full message so the just-sent email renders as HTML immediately,
        // rather than showing plain text until the next full events refresh.
        // Mirrors the incoming-message handling that fetches the HTML version.
        if (eventData.DTOSubtype === 'OmniEmailMessageTicketEvent') {
            void dispatch(fetchTicketMessage(ticketKey, eventData.key));
        }
        if (attemptDeleteFromLocalStorage) {
            deleteFailedMessageEventFromLocalStorage(ticketKey, placeholderKey);
        }
    };

const onUnrecoverableSendError =
    (ticketKey: TicketKey, messageKey: ConversationMessagePlaceholderKey) =>
    (dispatch: AppDispatch) => {
        dispatch(removeFailedPlaceholderMessageEvent(ticketKey, messageKey));
    };

export const removeFailedPlaceholderMessageEvent =
    (ticketKey: TicketKey, messageKey: ConversationMessagePlaceholderKey) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: Actions.REMOVE_TICKET_EVENT,
            ticketKey,
            eventKey: messageKey,
        });
        deleteFailedMessageEventFromLocalStorage(ticketKey, messageKey);
    };

const getTicketFailedEventsDataFromLocalStorage = (): Record<
    TicketKey,
    Record<ConversationMessagePlaceholderKey, PlaceholderMessage>
> => {
    if (!browserSupportStorage()) return {};
    const stored = wrappedLocalStorage.getItem(
        TICKET_FAILED_EVENTS_LOCAL_STORAGE_KEY,
    );
    if (!stored) return {};
    try {
        return JSON.parse(stored);
    } catch {
        return {};
    }
};

const saveFailedPlaceholderEventsRootObject = (
    rootObject: Record<
        TicketKey,
        Record<ConversationMessagePlaceholderKey, PlaceholderMessage>
    >,
) => {
    if (!browserSupportStorage()) return;
    wrappedLocalStorage.setItem(
        TICKET_FAILED_EVENTS_LOCAL_STORAGE_KEY,
        JSON.stringify(rootObject),
    );
};

const deleteFailedMessageEventFromLocalStorage = (
    ticketKey: TicketKey,
    placeholderKey: ConversationMessagePlaceholderKey,
) => {
    try {
        if (!browserSupportStorage()) return;
        const rootObject = getTicketFailedEventsDataFromLocalStorage();
        if (!rootObject[ticketKey]) return;
        delete rootObject[ticketKey][placeholderKey];

        if (Object.keys(rootObject[ticketKey]).length === 0) {
            delete rootObject[ticketKey];
        }
        saveFailedPlaceholderEventsRootObject(rootObject);
    } catch (error) {
        return;
    }
};

const saveFailedMessageEventToLocalStorage = (
    ticketKey: TicketKey,
    placeholderKey: ConversationMessagePlaceholderKey,
    placeholderMessageEvent: PlaceholderMessage,
) => {
    try {
        if (!browserSupportStorage()) return;
        const rootObject = getTicketFailedEventsDataFromLocalStorage();
        if (!rootObject[ticketKey]) {
            rootObject[ticketKey] = {};
        }

        const ticketKeys = Object.keys(rootObject);
        if (ticketKeys?.length >= 5) {
            // Remove the first (oldest) ticketKey and its entries
            delete rootObject[ticketKeys[0] as TicketKey];
        }

        // If the current ticketKey contains 10 or more entries, remove the first (oldest) entry
        const failedEventsPlaceholderData = Object.keys(rootObject[ticketKey]);
        if (failedEventsPlaceholderData?.length >= 10) {
            delete rootObject[ticketKey][
                failedEventsPlaceholderData[0] as ConversationMessagePlaceholderKey
            ];
        }

        rootObject[ticketKey][placeholderKey] = placeholderMessageEvent;
        saveFailedPlaceholderEventsRootObject(rootObject);
    } catch (_error) {
        return;
    }
};

const getPlaceholderEventRecordsFromLocalStorage = (
    ticketKey: TicketKey,
): Record<ConversationMessagePlaceholderKey, PlaceholderMessage> | null => {
    try {
        if (!browserSupportStorage()) return null;
        const rootObject = getTicketFailedEventsDataFromLocalStorage();
        if (!rootObject[ticketKey]) return null;
        return rootObject[ticketKey];
    } catch (_error) {
        return null;
    }
};

export const getPlaceholderEventsFromLocalStorage = (ticketKey: TicketKey) => {
    try {
        const eventRecords =
            getPlaceholderEventRecordsFromLocalStorage(ticketKey);
        if (!eventRecords) return [];
        const events: PlaceholderMessage[] = [];
        Object.entries(eventRecords).forEach(([_placeholderKey, event]) => {
            events.push(event);
        });
        return events;
    } catch (_error) {
        return [];
    }
};

const onMessageFailedToSend =
    (
        ticketKey: TicketKey,
        placeholderKey: ConversationMessagePlaceholderKey,
        placeholderMessageDto?: PlaceholderMessage,
    ) =>
    (dispatch: AppDispatch) => {
        dispatch(setTicketMessageStatus(ticketKey, placeholderKey, 'FAILED'));
        if (placeholderMessageDto) {
            saveFailedMessageEventToLocalStorage(ticketKey, placeholderKey, {
                ...placeholderMessageDto,
                status: 'FAILED',
            });
        }
    };

const onUploadAttachmentStageCompleted =
    (
        ticketKey: TicketKey,
        placeholderMessageKey: ConversationMessagePlaceholderKey,
        attachmentData: TicketAttachmentPlaceholder,
    ) =>
    (dispatch: AppDispatch) =>
        dispatch({
            type: Actions.UPLOAD_FILE_STAGE_IN_SEND_TICKET_MESSAGE_FINISHED,
            ticketKey,
            placeholderKey: placeholderMessageKey,
            attachmentData,
        });

const getSenderDataForPlaceholderMessage = (
    sender: ExtensionKey,
): SenderData => {
    return {
        sender: sender,
        senderType: 'ACCOUNT',
    };
};

type MentioneeFullNameAndKey = {
    fullName: string;
    extensionKey: ExtensionKey;
    chatUserKey: ChatUserKey;
};
type MentioneesFullNames = Record<
    ExtensionKey,
    TypedMap<MentioneeFullNameAndKey>
>;

const mentionFilter = (filterText: string, fullName: string) => {
    return new RegExp('(^|\\s)' + escapeRegExp(filterText), 'i').test(fullName);
};

const getFullNamesMap = (
    state: State,
    extensionKeys: ImmutableSet<ExtensionKey>,
): TypedMap<MentioneesFullNames> =>
    extensionKeys.reduce((acc: any, extensionKey) => {
        const fullName = getContactName(selectContact(state, extensionKey));
        const chatUserKey = selectChatUserKey(state, extensionKey);
        if (!fullName || !chatUserKey) return acc;
        return acc.set(
            extensionKey,
            Map({
                fullName: fullName,
                extensionKey: extensionKey,
                chatUserKey: chatUserKey,
            }),
        );
    }, Map());

const findMentionee = (
    fullNames: TypedMap<MentioneesFullNames>,
    possibleMention: string,
): TypedMap<MentioneeFullNameAndKey> | undefined => {
    // Go through the possible mention, one more character at a time
    for (let i = 2; i <= possibleMention.length; i++) {
        const filterText = possibleMention.substring(1, i);

        const filteredMentionees = fullNames.filter(
            (mentionee: TypedMap<MentioneeFullNameAndKey>) =>
                mentionFilter(filterText, mentionee.get('fullName')),
        );
        if (filteredMentionees.size > 1) {
            // If there are multiple possible matches, continue by adding another character
            continue;
        } else if (filteredMentionees.size === 0) {
            // If there are no matches, stop and try to find another possible mention
            return undefined;
        }

        const mentionee: TypedMap<MentioneeFullNameAndKey> =
            filteredMentionees.first();

        const mentioneeFullName = mentionee.get('fullName');
        if (mentioneeFullName.length !== filterText.length) {
            continue;
        }
        return mentioneeFullName === filterText ? mentionee : undefined;
    }
    return undefined;
};

// A pick applies only where the text still holds its "@name" at the index it
// was inserted at, so an edited name falls back to the search by name.
const findPickedMentionee = (
    state: State,
    pickedMentions: List<TypedMap<PickedMention>>,
    text: string,
    mentionStart: number,
): TypedMap<MentioneeFullNameAndKey> | undefined => {
    const pickedMention = pickedMentions.find(
        (pick) =>
            pick.get('start') === mentionStart &&
            text.startsWith(`@${pick.get('name')}`, mentionStart),
    );
    if (!pickedMention) return undefined;

    const extensionKey = pickedMention.get('extensionKey');
    const chatUserKey = selectChatUserKey(state, extensionKey);
    if (!chatUserKey) return undefined;

    return fromJS({
        fullName: pickedMention.get('name'),
        extensionKey: extensionKey,
        chatUserKey: chatUserKey,
    });
};

// NOTE:
// This method is 90% copied from Flow-webapp/src/app/flow/utils/message-utils.js createMentionDTOsFromText.
// The difference: the colleagues picked in the mention picker come first. For
// a name typed by hand, when the visibility allows it, the search covers all
// extensions next. Thus "@Anna Bergman" does not stop at a member "Anna Berg".
// Then the members break a tie, so a member wins over a colleague with the
// same name.
const createMentionDTO = (
    state: State,
    ticketKey: TicketKey,
    text: string,
    pickedMentions?: List<TypedMap<PickedMention>>,
) => {
    const memberKeys = selectMembers(state, ticketKey);
    const nonMembersAreMentionable = selectNonMembersAreMentionable(
        state,
        ticketKey,
    );
    const pickedMentionsAllowed = pickedMentions
        ?.filter(
            (pickedMention) =>
                nonMembersAreMentionable ||
                memberKeys?.has(pickedMention.get('extensionKey')),
        )
        .toList();
    const membersFullNames = getFullNamesMap(state, memberKeys);
    const extensionsFullNames = nonMembersAreMentionable
        ? getFullNamesMap(state, selectExtensions(state).keySeq().toSet())
        : undefined;
    let mentions: List<TypedMap<MessageStyling>> = List();
    let currentMentionPosition = text.length;
    const regexp = new RegExp(REGEXP_POSSIBLE_MENTION_STRICT);
    for (;;) {
        // Find a possible mention
        const regexpResult = regexp.exec(text);
        if (regexpResult == null) {
            return mentions;
        }
        const possibleMention = regexpResult[1];
        currentMentionPosition -= possibleMention.length;

        const mentionee =
            (pickedMentionsAllowed &&
                findPickedMentionee(
                    state,
                    pickedMentionsAllowed,
                    text,
                    currentMentionPosition,
                )) ??
            (extensionsFullNames &&
                findMentionee(extensionsFullNames, possibleMention)) ??
            findMentionee(membersFullNames, possibleMention);
        if (mentionee) {
            const length = mentionee.get('fullName').length + 1;
            const mentionDTO: TypedMap<MessageStyling> = fromJS({
                startPosition: currentMentionPosition,
                length: length,
                stylingReference: {
                    stylingType: 'chatuser',
                    chatUser: { key: mentionee.get('chatUserKey') },
                },
            });
            mentions = mentions.push(mentionDTO);
        }
        text = text.substring(0, currentMentionPosition);
    }
};

export const setTicketReplyDraft =
    (
        ticketKey: TicketKey,
        replyType: TicketReplyType,
        text: string,
        mentions?: List<TypedMap<PickedMention>>,
    ) =>
    (dispatch: AppDispatch) => {
        dispatch({
            type: Actions.SET_TICKET_REPLY_DRAFT,
            ticketKey,
            replyType,
            text,
            mentions,
        });
    };
